import React, { useEffect, useRef, useState } from "react";
import HeroHUD from "../Motion";
import { IMAGES } from "../../lib/content";
import { extractYouTubeId } from "../../lib/youtube";
import Countdown from "./Countdown";

// Muted looping YouTube backdrop via IFrame API — reports onError (embed disabled, removed, etc.)
function YouTubeLoop({ id, onError }) {
  const ref = useRef(null);
  useEffect(() => {
    let player;
    const init = () => {
      if (!ref.current) return;
      player = new window.YT.Player(ref.current, {
        videoId: id,
        host: "https://www.youtube-nocookie.com",
        width: "100%",
        height: "100%",
        playerVars: { autoplay: 1, mute: 1, loop: 1, playlist: id, controls: 0, rel: 0, modestbranding: 1, playsinline: 1, disablekb: 1, iv_load_policy: 3 },
        events: {
          onReady: (e) => { e.target.mute(); e.target.playVideo(); },
          onError: (e) => { console.warn("[hero-trailer] yt error", e?.data); onError(); },
        },
      });
    };
    if (window.YT?.Player) init();
    else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); init(); };
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(s);
      }
    }
    return () => { try { player?.destroy(); } catch { /* noop */ } };
  }, [id, onError]);

  return (
    <div className="absolute inset-0 overflow-hidden opacity-60 pointer-events-none" data-testid="reality-hero-youtube">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-[177.78vh] h-[56.25vw]">
        <div ref={ref} className="w-full h-full" />
      </div>
    </div>
  );
}

// Background: mp4/webm URL → <video loop>, YouTube link/ID → muted looping player, else image
function HeroBackdrop({ src }) {
  const s = String(src || "").trim();
  const isFile = /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(s);
  const ytId = !isFile ? extractYouTubeId(s) : "";
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [s]);

  if (isFile && !failed) {
    return (
      <video
        data-testid="reality-hero-video"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
        src={s}
        poster={IMAGES.fire}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
      />
    );
  }
  if (ytId && !failed) {
    return <YouTubeLoop id={ytId} onError={() => setFailed(true)} />;
  }
  return (
    <div
      data-testid="reality-hero-image"
      className="absolute inset-0 kenburns opacity-60"
      style={{ backgroundImage: `url(${IMAGES.fire})`, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(0.35) contrast(1.1)" }}
    />
  );
}

function Meta({ k, v, s, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#F1EDE6]/50">{k}</div>
      <div className="mt-2 condensed text-[40px] md:text-[48px] text-[#F1EDE6]">{v}</div>
      <div className="mt-1 mono text-[10px] uppercase tracking-[0.2em] text-[#F1EDE6]/60">{s}</div>
    </div>
  );
}

export default function RealityHero({ t, onWatch, onTrailer }) {
  const hasTrailer = !!String(t.hero_video || "").trim();
  return (
    <section
      data-testid="reality-hero"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#0B0B0C] scanlines"
    >
      <HeroBackdrop src={t.hero_video} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/70 to-[#0B0B0C]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C]/80 via-transparent to-transparent" />
      <div className="grain-static" />
      <div className="hidden md:block"><HeroHUD /></div>

      {hasTrailer && (
        <div className="absolute inset-y-0 right-0 w-[42%] hidden lg:flex items-center justify-center">
          <button
            type="button"
            onClick={onTrailer}
            data-testid="reality-hero-play"
            aria-label={t.cta_trailer}
            className="group flex flex-col items-center gap-6 fade-up"
            style={{ animationDelay: "300ms" }}
          >
            <span className="relative w-40 h-40 rounded-full border border-[#F1EDE6]/50 bg-[#0B0B0C]/40 backdrop-blur flex items-center justify-center transition-all duration-500 group-hover:scale-105 group-hover:border-[color:var(--alta-red-soft)] group-hover:bg-[color:var(--alta-red)]/20">
              <span className="absolute inset-0 rounded-full border border-[color:var(--alta-red)]/40 animate-ping [animation-duration:2.4s]" />
              <span className="ml-3 border-y-[22px] border-y-transparent border-l-[36px] border-l-[#F1EDE6]" />
            </span>
            <span className="mono text-[10px] uppercase tracking-[0.36em] text-[#F1EDE6] group-hover:text-[color:var(--alta-red-soft)] transition-colors">{t.cta_trailer}</span>
          </button>
        </div>
      )}

      <div className="relative min-h-[100vh] flex items-start pt-[104px] md:pt-[120px] pb-16">
        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 fade-up">
          <div className="max-w-[1100px]">
            <div
              data-testid="reality-hero-pill"
              className="inline-flex items-center gap-3 border border-[color:var(--alta-red)]/70 bg-[color:var(--alta-red)]/15 px-4 py-2 mono text-[10px] uppercase tracking-[0.28em] text-[#F1EDE6] backdrop-blur-sm"
            >
              <span className="rec-dot" aria-hidden="true" />
              <span>{t.pill1}</span>
              <span className="text-[#F1EDE6]/50">/</span>
              <span>{t.pill2}</span>
              <span className="text-[#F1EDE6]/50">/</span>
              <span>{t.pill3}</span>
            </div>

            <div className="mt-6 mono text-[11px] uppercase tracking-[0.32em] text-[#F1EDE6]/70">{t.eyebrow}</div>

            <h1
              data-testid="reality-hero-title"
              className="mt-3 condensed text-[#F1EDE6] text-[84px] md:text-[136px] lg:text-[160px] flex items-end gap-4 md:gap-8 flex-wrap"
            >
              <span>
                {t.title1} <span className="text-[color:var(--alta-red-soft)]">{t.title2}</span>
              </span>
              <span className="condensed text-[24px] md:text-[36px] text-[#F1EDE6]/60 pb-2 md:pb-6 tracking-[0.2em] flicker">
                {t.title_sub}
              </span>
            </h1>

            <p
              data-testid="reality-hero-headline"
              className="mt-4 condensed text-[30px] md:text-[44px] text-[#F1EDE6] tracking-[0.04em]"
            >
              {t.headline}
            </p>
            <p className="mt-3 serif italic text-[20px] md:text-[26px] leading-tight text-[#F1EDE6]/80 max-w-[44ch]">
              {t.subline_prefix}
              <span className="text-[color:var(--alta-red-soft)]">{t.subline_accent}</span>
            </p>

            <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {hasTrailer && (
                <button
                  type="button"
                  onClick={onTrailer}
                  data-testid="reality-hero-cta-trailer"
                  className="btn-red-primary group !gap-3"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-current transition-transform duration-300 group-hover:scale-110">
                    <span className="ml-[2px] border-y-[5px] border-y-transparent border-l-[8px] border-l-current" />
                  </span>
                  <span>{t.cta_trailer}</span>
                </button>
              )}
              <a href={t.premiere_url || t.channel_url} target="_blank" rel="noreferrer" data-testid="reality-hero-cta-primary" className={`${hasTrailer ? "btn-outline-light" : "btn-red-primary"} group`}>
                <span>{t.cta_primary}</span>
                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
              <button
                type="button"
                onClick={onWatch}
                data-testid="reality-hero-cta-secondary"
                className="mono text-[11px] uppercase tracking-[0.28em] text-[#F1EDE6] hover:text-[color:var(--alta-red-soft)] transition-colors border-b border-[#F1EDE6]/40 hover:border-[color:var(--alta-red-soft)] pb-1"
              >
                {t.cta_secondary}
              </button>
            </div>

            <div className="mt-8">
              <Countdown t={t} />
            </div>

            <div className="mt-8 grid grid-cols-3 gap-6 md:gap-12 max-w-[760px] border-t border-[#F1EDE6]/15 pt-5">
              <Meta k={t.meta_1_k} v={t.meta_1_v} s={t.meta_1_s} testid="reality-meta-1" />
              <Meta k={t.meta_2_k} v={t.meta_2_v} s={t.meta_2_s} testid="reality-meta-2" />
              <Meta k={t.meta_3_k} v={t.meta_3_v} s={t.meta_3_s} testid="reality-meta-3" />
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
